import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Shield, FileText, Database, Cookie, ArrowLeft, Mail } from "lucide-react";
import BrandMark from "@/components/BrandMark";
import ThemeToggle from "@/components/ThemeToggle";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EFFECTIVE = "1 June 2026";
const CONTACT = "privacy@yanthralabs.com";

const DOCS = {
  privacy: {
    icon: Shield,
    title: "Privacy Policy",
    lede:
      "How Yanthra Labs collects, uses, stores and deletes personal data in MeetIQ360 — written to satisfy the EU GDPR and India's Digital Personal Data Protection Act, 2023.",
    sections: [
      ["Who we are", "MeetIQ360 is operated by Yanthra Labs. For meeting content we act as a **processor** on behalf of your organisation, which is the **controller** (under the DPDP Act, the **Data Fiduciary**). For your account and billing data we are the controller. Our grievance officer can be reached at " + CONTACT + "."],
      ["What we collect", "**Account data** — name, work email, role, department, designation and organisation. **Meeting content** — the audio/video you choose to record, the transcript derived from it, and the AI-generated summary, decisions, action items, risks and questions. **Billing data** — organisation name, billing contact, GSTIN, invoices and payment references. **Security logs** — user id, event, IP address, user agent and timestamp."],
      ["Why we process it", "To authenticate you and operate your workspace (contract); to transcribe and summarise meetings you record (your consent and your organisation's instruction); to charge for the service and issue tax invoices (contract and legal obligation); and to detect abuse (legitimate interests, GDPR Art.6(1)(f))."],
      ["Recording consent", "Nobody is recorded silently. Every guest joining a meeting must tick an explicit consent box before entering, and we store that consent with a timestamp against their participant record so the organiser can evidence it. You may refuse and leave at any time."],
      ["AI processing", "Transcription and summarisation are performed by sub-processors (OpenAI Whisper, Sarvam AI for Indic languages, and an LLM for minutes). Meeting content is sent for processing only; it is **not** used to train third-party models. Our AI assistant is scoped to your own workspace data and does not query the open internet."],
      ["Who we share it with", "Only the sub-processors listed in our record of processing activities, each under a data-processing agreement: OpenAI, Sarvam AI, HeyGen, LiveKit Cloud, Microsoft 365 (email delivery) and our payment processors. We never sell personal data and we do not use it for advertising."],
      ["International transfers", "Cross-border transfers rely on Standard Contractual Clauses for GDPR and are limited to countries not restricted under DPDP s.16. Enterprise customers may select a storage region (India, EU, US or Singapore)."],
      ["How long we keep it", "Recordings and transcripts are retained for 365 days by default, or until your organiser deletes them — whichever comes first. A weekly automated job purges media past that window. Account data is removed 30 days after the account closes. Invoices are kept for 8 years as tax law requires. Security logs are kept for 180 days."],
      ["Your rights", "You can **download everything we hold about you** and **erase your account and its artefacts** yourself, from Settings → Privacy & your data. You may also correct your profile, object to processing, withdraw recording consent, and lodge a complaint with your supervisory authority or the Data Protection Board of India."],
      ["Security", "Data is encrypted in transit (TLS) and at rest (AES-256). Access is role-based and strictly scoped to your organisation; passwords are hashed and never recoverable. We rate-limit authentication, log security events and review access regularly."],
      ["Children", "MeetIQ360 is a workplace product and is not directed at children. We do not knowingly process the personal data of anyone under 18."],
      ["Changes", "We will notify workspace administrators by email before any material change takes effect, and you will be asked to accept the new version on next sign-in."],
    ],
  },
  terms: {
    icon: FileText,
    title: "Terms of Service",
    lede: "The agreement between your organisation and Yanthra Labs for the use of MeetIQ360.",
    sections: [
      ["The service", "MeetIQ360 records meetings you choose to record, transcribes them, validates the transcript and produces minutes, decisions and action items. We provide the service on a subscription basis per seat, per month."],
      ["Your account", "You are responsible for the accuracy of your account details, for keeping credentials confidential, and for all activity under your account. Workspace administrators control who may join and what each member can do."],
      ["Acceptable use", "You must not use MeetIQ360 to record anyone without their knowledge or consent, to process data you have no right to process, to reverse engineer or resell the service, to probe or circumvent its security, or to upload unlawful or infringing content."],
      ["Lawful recording", "Recording laws differ by jurisdiction and some require the consent of every participant. You are responsible for obtaining consent where the law requires it. We give you the tools — an explicit consent gate and a timestamped consent record — but the obligation is yours."],
      ["AI output", "Transcription and AI minutes are produced by machine and can contain errors. Our Quality Guard tells you when the audio was not good enough to rely on. AI output is decision support, not a certified record; the chair's corrections prevail."],
      ["Fees and taxes", "Subscriptions are billed per seat in advance. Indian customers are billed in INR with GST applied as required; other customers are billed in USD and are responsible for any local tax. Fees are non-refundable except where law requires otherwise."],
      ["Availability", "We target 99.9% monthly availability excluding scheduled maintenance. The service is otherwise provided on an as-is basis without warranties beyond those that cannot lawfully be excluded."],
      ["Liability", "To the maximum extent permitted by law, neither party is liable for indirect or consequential loss, and our aggregate liability is capped at the fees you paid in the twelve months before the claim."],
      ["Term and termination", "Either party may terminate at the end of a billing period. We may suspend access immediately for non-payment or for a breach of the acceptable-use terms. On termination you may export your data; we then delete it per the retention schedule in our Privacy Policy."],
      ["Governing law", "These terms are governed by the laws of India, with courts at Chennai, Tamil Nadu having exclusive jurisdiction, unless a mandatory local law in your jurisdiction provides otherwise."],
    ],
  },
  dpa: {
    icon: Database,
    title: "Data Processing Addendum",
    lede: "The processor commitments Yanthra Labs makes to your organisation. Forms part of the Terms of Service.",
    sections: [
      ["Roles", "Your organisation is the controller / Data Fiduciary. Yanthra Labs is the processor / Data Processor for all meeting content. We process personal data only on your documented instructions."],
      ["Subject matter and duration", "Processing covers the recording, transcription, analysis and storage of meetings for the duration of your subscription, plus the retention windows set out in the Privacy Policy."],
      ["Categories of data subjects", "Your employees, contractors and any external meeting guests you invite."],
      ["Our obligations", "We will: implement appropriate technical and organisational measures; impose equivalent obligations on every sub-processor; assist you with data-subject requests and impact assessments; notify you without undue delay — and in any case within 72 hours — of a personal-data breach; and delete or return data on termination."],
      ["Sub-processors", "A current list is published at /api/privacy/processing-record. We will give workspace administrators notice before adding a sub-processor, and you may object on reasonable data-protection grounds."],
      ["Security measures", "TLS in transit, AES-256 at rest, role-based access strictly scoped per organisation, hashed credentials, authentication rate limiting, audit logging, least-privilege infrastructure access and periodic review."],
      ["Audit", "On reasonable notice and no more than once a year, you may request our security documentation and the results of our most recent review."],
      ["Transfers", "Where data leaves the EEA or India we rely on Standard Contractual Clauses and equivalent safeguards, and we honour region selection for enterprise plans."],
      ["Deletion", "On termination, or on your written instruction, we delete meeting content within 30 days except where retention is required by law."],
    ],
  },
  cookies: {
    icon: Cookie,
    title: "Cookies & Local Storage",
    lede: "MeetIQ360 uses no advertising or third-party tracking cookies.",
    sections: [
      ["What we store on your device", "**meetiq_token** — your sign-in session. **meetiq_theme** — light or dark preference. **meetiq_locale** — interface language. **meetiq_region** — the currency shown on pricing. **meetiq_nav_collapsed** and **meetiq_tour_done_v1** — small interface preferences. All of these are strictly necessary or purely functional, and all are stored in your browser's local storage rather than as tracking cookies."],
      ["What we do not do", "We set no advertising cookies, run no cross-site trackers, and do not sell or share browsing data. There is no third-party analytics pixel on the application."],
      ["Clearing them", "Signing out removes your session. Clearing site data in your browser removes everything above; you will simply be asked to sign in and choose your preferences again."],
    ],
  },
};

const ORDER = ["privacy", "terms", "dpa", "cookies"];

function rich(text) {
  return text.split(/(\*\*[^*]+\*\*)/g).map((part, i) =>
    part.startsWith("**") && part.endsWith("**")
      ? <strong key={i} className="font-semibold text-foreground">{part.slice(2, -2)}</strong>
      : <span key={i}>{part}</span>
  );
}

export default function Legal() {
  const { doc } = useParams();
  const key = DOCS[doc] ? doc : "privacy";
  const d = DOCS[key];
  const Icon = d.icon;
  const [record, setRecord] = useState(null);

  useEffect(() => {
    axios.get(`${API}/privacy/processing-record`).then(({ data }) => setRecord(data)).catch(() => {});
  }, []);

  return (
    <div data-testid="legal-root" className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-30 border-b border-border bg-background/85 backdrop-blur-xl">
        <div className="mx-auto max-w-[1000px] px-5 sm:px-8 h-16 flex items-center gap-4">
          <Link to="/home" data-testid="legal-home-link"><BrandMark size="sm" showByline={false} /></Link>
          <Link
            to="/home"
            className="ml-auto inline-flex items-center gap-1.5 text-xs font-bold text-muted-foreground hover:text-primary transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" strokeWidth={2.2} /> Back
          </Link>
          <ThemeToggle testid="legal-theme-toggle" />
        </div>
      </header>

      <div className="mx-auto max-w-[1000px] px-5 sm:px-8 py-12 lg:py-16">
        <div className="flex flex-wrap gap-2 mb-10" data-testid="legal-nav">
          {ORDER.map((k) => (
            <Link
              key={k}
              to={`/legal/${k}`}
              data-testid={`legal-tab-${k}`}
              className={`px-4 py-2 rounded-full text-xs font-bold border transition-colors ${
                k === key
                  ? "bg-primary text-primary-foreground border-transparent"
                  : "border-border text-foreground/70 hover:border-primary/50"
              }`}
            >
              {DOCS[k].title}
            </Link>
          ))}
        </div>

        <div className="w-11 h-11 rounded-2xl grid place-items-center" style={{ background: "hsl(var(--cyan) / .13)" }}>
          <Icon className="w-5 h-5" style={{ color: "hsl(var(--ink-info))" }} strokeWidth={1.9} />
        </div>
        <div className="eyebrow mt-6">Effective {EFFECTIVE} · version {record?.policy_version || "2026-06-01"}</div>
        <h1 className="font-heading text-4xl sm:text-5xl mt-4 leading-[1.06]" data-testid="legal-title">
          {d.title}
        </h1>
        <p className="text-base md:text-lg text-muted-foreground mt-5 max-w-2xl leading-relaxed">{d.lede}</p>

        <div className="mt-12 space-y-10">
          {d.sections.map(([h, body], i) => (
            <section key={h} data-testid={`legal-section-${i}`} className="yl-beam pt-6 border-t border-border">
              <h2 className="font-heading text-lg md:text-xl">{String(i + 1).padStart(2, "0")}. {h}</h2>
              <p className="text-sm md:text-base text-muted-foreground mt-3 leading-relaxed">{rich(body)}</p>
            </section>
          ))}
        </div>

        {key === "privacy" && record && (
          <section className="yl-surface p-6 lg:p-8 mt-12" data-testid="legal-processing-record">
            <h2 className="font-heading text-xl">Record of processing activities</h2>
            <p className="text-xs text-muted-foreground mt-2">
              Published under GDPR Art.30. Retention window: {record.retention_days} days.
            </p>
            <div className="mt-6 overflow-x-auto yl-scroll">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="bg-secondary/60">
                  <tr>
                    {["Category", "Purpose", "Lawful basis", "Retention"].map((h) => (
                      <th key={h} className="text-left px-3 py-2.5 eyebrow text-[9px]">{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(record.categories || []).map((c) => (
                    <tr key={c.category} className="border-b border-border">
                      <td className="px-3 py-3 font-semibold">{c.category}</td>
                      <td className="px-3 py-3 text-muted-foreground">{c.purpose}</td>
                      <td className="px-3 py-3 text-muted-foreground">{c.lawful_basis}</td>
                      <td className="px-3 py-3 text-muted-foreground">{c.retention}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="eyebrow text-[9px] mt-8">Sub-processors</div>
            <div className="flex flex-wrap gap-2 mt-3">
              {(record.sub_processors || []).map((sp) => (
                <span key={sp.name} className="rounded-full border border-border bg-card px-3 py-1.5 text-[11px] font-semibold">
                  {sp.name} · {sp.region}
                </span>
              ))}
            </div>
          </section>
        )}

        <div className="yl-surface p-6 mt-12 flex flex-wrap items-center gap-4 justify-between">
          <div>
            <div className="eyebrow text-[9px]">Questions or a data request?</div>
            <p className="text-sm mt-1.5">
              Our grievance officer answers within 30 days, as the DPDP Act requires.
            </p>
          </div>
          <a
            href={`mailto:${CONTACT}`}
            data-testid="legal-contact"
            className="inline-flex items-center gap-2 h-11 px-5 rounded-full bg-primary text-primary-foreground text-sm font-bold hover:bg-[hsl(var(--primary-hover))] transition-colors"
          >
            <Mail className="w-4 h-4" strokeWidth={2.2} /> {CONTACT}
          </a>
        </div>
      </div>
    </div>
  );
}
